import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { motion } from "framer-motion";
import { FileCheck, ArrowLeft, Upload, CheckCircle, XCircle, ArrowRight, AlertTriangle, Sparkles } from "lucide-react";
import axios from "axios";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResumeCheckerPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);

  const handlePasteCheck = async () => {
    if (!text.trim()) return;
    setLoading(true); setResult(null);
    const token = localStorage.getItem("buddy_access_token");
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/tools/resume-check`, { text: text.trim() }, { headers });
      setResult(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Something went wrong.");
      setResult({ error: true, message: msg });
    }
    finally { setLoading(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setLoading(true); setResult(null);
    const formData = new FormData();
    formData.append("file", file);
    const token = localStorage.getItem("buddy_access_token");
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/tools/resume-upload`, formData, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      setResult(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Failed to process file");
      setResult({ error: true, message: msg });
    } finally { setLoading(false); }
  };

  const sc = result?.ats_score || 0;
  const scoreColor = sc >= 80 ? "var(--rolex-green)" : sc >= 60 ? "var(--rolex-gold)" : "var(--error)";
  const breakdown = result?.score_breakdown || {};

  return (
    <div className="min-h-screen" style={{ background: "var(--surface-cream)" }}>
      {user ? <Navbar /> : (
        <nav className="sticky top-0 z-50" style={{ background: "rgba(250,250,248,0.8)", backdropFilter: "blur(16px)", borderBottom: "1px solid rgba(232,228,220,0.6)" }}>
          <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
            <h1 className="font-display text-3xl font-bold cursor-pointer" style={{ color: "var(--rolex-green)" }} onClick={() => navigate("/")}>Buddy</h1>
            <button onClick={() => navigate("/login")} className="btn-primary py-2 px-5 text-sm">Sign In</button>
          </div>
        </nav>
      )}
      <main className="max-w-5xl mx-auto px-4 pt-6 pb-16">
        <button onClick={() => navigate("/ai-tools")} className="flex items-center gap-1 text-sm mb-4 hover:underline" style={{ color: "var(--rolex-green)" }}><ArrowLeft size={14} /> AI Tools</button>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#E8F2EC" }}><FileCheck size={20} style={{ color: "#5B8A72" }} /></div>
            <div><h1 className="font-display text-2xl font-bold" style={{ color: "var(--text-primary)" }}>AI Resume Checker</h1><p className="text-sm" style={{ color: "var(--text-muted)" }}>ATS compatibility analysis with actionable tips to reach 90%+</p></div>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Input */}
          <div className="space-y-4">
            <div className="rounded-2xl p-6" style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)" }}>
              <span className="label-caps block mb-3">Upload Resume</span>
              <input type="file" ref={fileRef} accept=".pdf,.docx,.doc,.txt" className="hidden" onChange={handleFileUpload} data-testid="resume-file-input" />
              <motion.button onClick={() => fileRef.current?.click()} className="w-full py-8 rounded-xl border-2 border-dashed flex flex-col items-center gap-2 transition-colors hover:bg-white" style={{ borderColor: "var(--surface-border-mid)" }} whileHover={{ borderColor: "var(--rolex-green)" }} data-testid="resume-upload-btn">
                <Upload size={28} style={{ color: "var(--rolex-green)" }} />
                <span className="text-sm font-medium" style={{ color: "var(--text-secondary)" }}>Click to upload PDF, DOCX, or TXT</span>
                {fileName && <span className="text-xs" style={{ color: "var(--rolex-green)" }}>{fileName}</span>}
              </motion.button>
            </div>
            <div className="rounded-2xl p-6" style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)" }}>
              <span className="label-caps block mb-2">Or Paste Resume Text</span>
              <textarea className="buddy-input resize-none text-xs" style={{ minHeight: "160px" }} rows={8} placeholder="Paste your entire resume text here..." value={text} onChange={(e) => setText(e.target.value)} data-testid="resume-text-input" />
              <motion.button onClick={handlePasteCheck} disabled={!text.trim() || loading} className="btn-primary py-2.5 px-6 text-sm mt-3 w-full" style={{ opacity: !text.trim() ? 0.5 : 1 }} data-testid="resume-check-btn">
                {loading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto block" /> : "Analyze Resume"}
              </motion.button>
            </div>
          </div>

          {/* Result */}
          <div className="rounded-2xl p-6" style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(12px)", border: "1px solid rgba(232,228,220,0.5)", minHeight: "400px" }}>
            {loading ? <div className="space-y-3 mt-4"><div className="h-4 rounded animate-shimmer" style={{ width: "80%" }} /><div className="h-4 rounded animate-shimmer" style={{ width: "60%" }} /><div className="h-4 rounded animate-shimmer" style={{ width: "70%" }} /></div>
            : result && !result.error ? (
              <div className="space-y-5">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full flex flex-col items-center justify-center" style={{ border: `3px solid ${scoreColor}` }}>
                    <span className="text-2xl font-bold" style={{ color: scoreColor }}>{sc}</span><span className="text-[9px]" style={{ color: "var(--text-muted)" }}>ATS</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>ATS Score</p>
                    <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full" style={{ background: sc >= 80 ? "var(--success-bg)" : sc >= 60 ? "var(--warning-bg)" : "var(--error-bg)", color: sc >= 80 ? "var(--success)" : sc >= 60 ? "var(--warning)" : "var(--error)" }}>{result.pass_likelihood?.toUpperCase()} PASS</span>
                  </div>
                </div>
                {/* Score breakdown */}
                {Object.keys(breakdown).length > 0 && (
                  <div>
                    <span className="label-caps block mb-2">Score Breakdown</span>
                    <div className="grid grid-cols-5 gap-2">
                      {Object.entries(breakdown).map(([k, v]) => (
                        <div key={k} className="text-center p-2 rounded-lg" style={{ background: "var(--surface-warm)" }}>
                          <p className="text-lg font-bold" style={{ color: v >= 8 ? "var(--rolex-green)" : v >= 6 ? "var(--rolex-gold)" : "var(--error)" }}>{v}</p>
                          <p className="text-[9px]" style={{ color: "var(--text-muted)" }}>{k}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {result.improvement_plan && (
                  <div className="p-3 rounded-xl" style={{ background: "rgba(0,96,57,0.06)", border: "1px solid rgba(0,96,57,0.15)" }}>
                    <span className="text-[10px] font-bold flex items-center gap-1 mb-2" style={{ color: "var(--rolex-green)" }}><Sparkles size={11} /> PATH TO 90%+</span>
                    <ul className="space-y-1.5">
                      {result.improvement_plan.split(/\.\s+|Step \d+:|[\n\r]+/).filter(s => s.trim()).map((step, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                          <span className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5" style={{ background: "var(--rolex-green)" }} />
                          {step.trim()}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {result.strengths?.length > 0 && <div><span className="label-caps block mb-1.5">Strengths</span>{result.strengths.map((s, i) => <div key={i} className="flex items-start gap-2 mb-1"><CheckCircle size={13} style={{ color: "var(--rolex-green)" }} className="shrink-0 mt-0.5" /><span className="text-xs" style={{ color: "var(--text-muted)" }}>{s}</span></div>)}</div>}
                {result.issues?.length > 0 && <div><span className="label-caps block mb-1.5">Issues to Fix</span>{result.issues.map((iss, i) => <div key={i} className="p-2.5 rounded-lg mb-2" style={{ background: "var(--surface-warm)" }}><p className="text-xs font-medium" style={{ color: "var(--text-primary)" }}>{iss.issue}</p><p className="text-xs mt-0.5" style={{ color: "var(--rolex-green)" }}>{iss.fix}</p></div>)}</div>}
                {result.recommendations?.length > 0 && <div><span className="label-caps block mb-1.5">Recommendations</span>{result.recommendations.map((r, i) => <div key={i} className="flex items-start gap-2 mb-1"><ArrowRight size={13} style={{ color: "var(--rolex-gold)" }} className="shrink-0 mt-0.5" /><span className="text-xs" style={{ color: "var(--text-muted)" }}>{r}</span></div>)}</div>}
              </div>
            ) : result?.error ? <p className="text-sm p-4 rounded-xl" style={{ background: "var(--error-bg)", color: "var(--error)" }}>{result.message || "Something went wrong."}</p>
            : <div className="flex flex-col items-center justify-center py-16"><FileCheck size={36} style={{ color: "var(--surface-border-mid)" }} /><p className="text-sm mt-3" style={{ color: "var(--text-light)" }}>Upload or paste your resume to get started</p></div>}
          </div>
        </div>
      </main>
    </div>
  );
}
